import { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { format } from 'date-fns';
import {
  Clock, Plus, Search, Calendar, Users, Mail, Bell, Play, Pause,
  Trash2, Edit2, RefreshCw, Loader2, FileText, CheckCircle2, History
} from 'lucide-react';
import { scheduledReportsAPI } from '../api';
import api from '../api';

const ScheduledReportsPage = () => {
  const { hasRole } = useAuth();
  const [loading, setLoading] = useState(true);
  const [reports, setReports] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [assignableUsers, setAssignableUsers] = useState([]);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [editingReport, setEditingReport] = useState(null);
  const [runningReport, setRunningReport] = useState(null);

  const [formData, setFormData] = useState({
    name: '',
    campaign_ids: [],
    frequency: 'weekly',
    day_of_week: 0,
    day_of_month: 1,
    time_of_day: '09:00',
    assigned_users: [],
    notification_type: 'both',
    include_recommendations: true,
    include_learnings: true,
    is_active: true
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [reportsRes, campaignsRes, usersRes] = await Promise.all([
        scheduledReportsAPI.getAll(),
        api.get('/marketing/campaigns', { params: { limit: 100 } }),
        scheduledReportsAPI.getAssignableUsers()
      ]);
      setReports(reportsRes.data?.scheduled_reports || []);
      setCampaigns(campaignsRes.data || []);
      setAssignableUsers(usersRes.data?.users || []);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load scheduled reports');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      campaign_ids: [],
      frequency: 'weekly',
      day_of_week: 0,
      day_of_month: 1,
      time_of_day: '09:00',
      assigned_users: [],
      notification_type: 'both',
      include_recommendations: true,
      include_learnings: true,
      is_active: true
    });
    setEditingReport(null);
  };

  const handleCreateOrUpdate = async () => {
    if (!formData.name.trim()) {
      toast.error('Report name is required');
      return;
    }
    if (formData.campaign_ids.length === 0) {
      toast.error('Select at least one campaign');
      return;
    }

    try {
      if (editingReport) {
        await scheduledReportsAPI.update(editingReport.id, formData);
        toast.success('Scheduled report updated');
      } else {
        await scheduledReportsAPI.create(formData);
        toast.success('Scheduled report created');
      }
      setShowCreateDialog(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error('Failed to save scheduled report');
    }
  };

  const handleEdit = (report) => {
    setFormData({
      name: report.name || '',
      campaign_ids: report.campaign_ids || [],
      frequency: report.frequency || 'weekly',
      day_of_week: report.day_of_week ?? 0,
      day_of_month: report.day_of_month ?? 1,
      time_of_day: report.time_of_day || '09:00',
      assigned_users: report.assigned_users || [],
      notification_type: report.notification_type || 'both',
      include_recommendations: report.include_recommendations ?? true,
      include_learnings: report.include_learnings ?? true,
      is_active: report.is_active ?? true
    });
    setEditingReport(report);
    setShowCreateDialog(true);
  };

  const handleToggle = async (reportId) => {
    try {
      await scheduledReportsAPI.toggle(reportId);
      toast.success('Report status updated');
      fetchData();
    } catch (error) {
      toast.error('Failed to toggle report');
    }
  };

  const handleDelete = async (reportId) => {
    if (!window.confirm('Are you sure you want to delete this scheduled report?')) return;
    try {
      await scheduledReportsAPI.delete(reportId);
      toast.success('Scheduled report deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete report');
    }
  };

  const handleRunNow = async (reportId) => {
    try {
      setRunningReport(reportId);
      const res = await scheduledReportsAPI.runNow(reportId);
      toast.success(`Report generated! Notified ${res.data?.notifications_sent || 0} user(s)`);
      fetchData();
    } catch (error) {
      toast.error('Failed to run report');
    } finally {
      setRunningReport(null);
    }
  };

  const toggleCampaign = (campaignId) => {
    setFormData(prev => ({
      ...prev,
      campaign_ids: prev.campaign_ids.includes(campaignId)
        ? prev.campaign_ids.filter(id => id !== campaignId)
        : [...prev.campaign_ids, campaignId]
    }));
  };

  const toggleUser = (userId) => {
    setFormData(prev => ({
      ...prev,
      assigned_users: prev.assigned_users.includes(userId)
        ? prev.assigned_users.filter(id => id !== userId)
        : [...prev.assigned_users, userId]
    }));
  };

  const DAYS_OF_WEEK = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

  return (
    <div className="space-y-6" data-testid="scheduled-reports-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Clock className="h-6 w-6" />
            Scheduled Reports
          </h1>
          <p className="text-muted-foreground">Automate report generation and distribution</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={fetchData}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          {hasRole(['admin', 'marketing_manager']) && (
            <Button onClick={() => { resetForm(); setShowCreateDialog(true); }}>
              <Plus className="h-4 w-4 mr-2" />
              Create Schedule
            </Button>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Schedules</p>
                <p className="text-2xl font-bold">{reports.length}</p>
              </div>
              <Calendar className="h-8 w-8 text-blue-400" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Active</p>
                <p className="text-2xl font-bold">{reports.filter(r => r.is_active).length}</p>
              </div>
              <CheckCircle2 className="h-8 w-8 text-green-400" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Runs</p>
                <p className="text-2xl font-bold">{reports.reduce((sum, r) => sum + (r.run_count || 0), 0)}</p>
              </div>
              <History className="h-8 w-8 text-purple-400" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Recipients</p>
                <p className="text-2xl font-bold">
                  {new Set(reports.flatMap(r => r.assigned_users || [])).size}
                </p>
              </div>
              <Users className="h-8 w-8 text-amber-400" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Reports List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : reports.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Clock className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
            <h3 className="font-semibold mb-2">No Scheduled Reports</h3>
            <p className="text-muted-foreground mb-4">
              Create a schedule to automatically generate and distribute reports.
            </p>
            {hasRole(['admin', 'marketing_manager']) && (
              <Button onClick={() => { resetForm(); setShowCreateDialog(true); }}>
                <Plus className="h-4 w-4 mr-2" />
                Create Your First Schedule
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {reports.map(report => (
            <Card key={report.id} className="hover:shadow-md transition-shadow">
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="font-semibold text-lg">{report.name}</h3>
                      <Badge variant={report.is_active ? 'default' : 'secondary'}>
                        {report.is_active ? 'Active' : 'Paused'}
                      </Badge>
                      <Badge variant="outline" className="capitalize">{report.frequency}</Badge>
                    </div>
                    
                    <div className="flex items-center gap-4 text-sm text-muted-foreground mb-3">
                      <span className="flex items-center gap-1">
                        <FileText className="h-4 w-4" />
                        {report.campaign_names?.length || 0} campaign(s)
                      </span>
                      <span className="flex items-center gap-1">
                        <Users className="h-4 w-4" />
                        {report.assigned_users?.length || 0} recipient(s)
                      </span>
                      <span className="flex items-center gap-1">
                        <History className="h-4 w-4" />
                        {report.run_count || 0} runs
                      </span>
                      {report.notification_type === 'email' && <Mail className="h-4 w-4" />}
                      {report.notification_type === 'in_app' && <Bell className="h-4 w-4" />}
                      {report.notification_type === 'both' && (
                        <>
                          <Mail className="h-4 w-4" />
                          <Bell className="h-4 w-4" />
                        </>
                      )}
                    </div>

                    <div className="flex items-center gap-4 text-sm">
                      {report.next_run && (
                        <span className="text-muted-foreground">
                          Next run: {format(new Date(report.next_run), 'MMM d, yyyy h:mm a')}
                        </span>
                      )}
                      {report.last_run && (
                        <span className="text-muted-foreground">
                          Last run: {format(new Date(report.last_run), 'MMM d, yyyy h:mm a')}
                        </span>
                      )}
                    </div>

                    {/* Campaign chips */}
                    <div className="flex flex-wrap gap-2 mt-3">
                      {report.campaign_names?.slice(0, 3).map((name, idx) => (
                        <Badge key={idx} variant="outline" className="text-xs">{name}</Badge>
                      ))}
                      {(report.campaign_names?.length || 0) > 3 && (
                        <Badge variant="outline" className="text-xs">+{report.campaign_names.length - 3} more</Badge>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-2 ml-4">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleRunNow(report.id)}
                      disabled={runningReport === report.id}
                    >
                      {runningReport === report.id ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Play className="h-4 w-4" />
                      )}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleToggle(report.id)}
                    >
                      {report.is_active ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => handleEdit(report)}>
                      <Edit2 className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-red-500 hover:text-red-600"
                      onClick={() => handleDelete(report.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={(open) => { if (!open) resetForm(); setShowCreateDialog(open); }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingReport ? 'Edit Scheduled Report' : 'Create Scheduled Report'}</DialogTitle>
            <DialogDescription>Configure automated report generation and distribution</DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Basic Info */}
            <div className="space-y-4">
              <div>
                <Label>Report Name *</Label>
                <Input
                  placeholder="Weekly Marketing Performance"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                />
              </div>
            </div>

            {/* Schedule */}
            <div className="space-y-4">
              <h4 className="font-semibold">Schedule</h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Frequency</Label>
                  <Select value={formData.frequency} onValueChange={(v) => setFormData({ ...formData, frequency: v })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="daily">Daily</SelectItem>
                      <SelectItem value="weekly">Weekly</SelectItem>
                      <SelectItem value="monthly">Monthly</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Time</Label>
                  <Input
                    type="time"
                    value={formData.time_of_day}
                    onChange={(e) => setFormData({ ...formData, time_of_day: e.target.value })}
                  />
                </div>
              </div>
              
              {formData.frequency === 'weekly' && (
                <div>
                  <Label>Day of Week</Label>
                  <Select value={String(formData.day_of_week)} onValueChange={(v) => setFormData({ ...formData, day_of_week: parseInt(v) })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {DAYS_OF_WEEK.map((day, idx) => (
                        <SelectItem key={idx} value={String(idx)}>{day}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              
              {formData.frequency === 'monthly' && (
                <div>
                  <Label>Day of Month</Label>
                  <Select value={String(formData.day_of_month)} onValueChange={(v) => setFormData({ ...formData, day_of_month: parseInt(v) })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Array.from({ length: 28 }, (_, i) => i + 1).map(day => (
                        <SelectItem key={day} value={String(day)}>{day}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>

            {/* Campaigns */}
            <div className="space-y-3">
              <h4 className="font-semibold">Campaigns to Include *</h4>
              <div className="max-h-40 overflow-y-auto border rounded-lg p-3 space-y-2">
                {campaigns.map(campaign => (
                  <div key={campaign.id} className="flex items-center gap-2">
                    <Checkbox
                      checked={formData.campaign_ids.includes(campaign.id)}
                      onCheckedChange={() => toggleCampaign(campaign.id)}
                    />
                    <span className="text-sm">{campaign.name}</span>
                    <Badge variant="outline" className="text-xs capitalize">{campaign.status}</Badge>
                  </div>
                ))}
                {campaigns.length === 0 && (
                  <p className="text-sm text-muted-foreground">No campaigns available</p>
                )}
              </div>
            </div>

            {/* Recipients */}
            <div className="space-y-3">
              <h4 className="font-semibold">Recipients</h4>
              <div className="max-h-32 overflow-y-auto border rounded-lg p-3 space-y-2">
                {assignableUsers.map(user => (
                  <div key={user.id} className="flex items-center gap-2">
                    <Checkbox
                      checked={formData.assigned_users.includes(user.id)}
                      onCheckedChange={() => toggleUser(user.id)}
                    />
                    <span className="text-sm">{user.name}</span>
                    <span className="text-xs text-muted-foreground">{user.email}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Notification Type */}
            <div>
              <Label>Notification Method</Label>
              <Select value={formData.notification_type} onValueChange={(v) => setFormData({ ...formData, notification_type: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="email">Email Only</SelectItem>
                  <SelectItem value="in_app">In-App Only</SelectItem>
                  <SelectItem value="both">Both Email & In-App</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Options */}
            <div className="space-y-3">
              <h4 className="font-semibold">Report Options</h4>
              <div className="flex items-center justify-between">
                <Label>Include Recommendations</Label>
                <Switch
                  checked={formData.include_recommendations}
                  onCheckedChange={(v) => setFormData({ ...formData, include_recommendations: v })}
                />
              </div>
              <div className="flex items-center justify-between">
                <Label>Include Learnings</Label>
                <Switch
                  checked={formData.include_learnings}
                  onCheckedChange={(v) => setFormData({ ...formData, include_learnings: v })}
                />
              </div>
              <div className="flex items-center justify-between">
                <Label>Active</Label>
                <Switch
                  checked={formData.is_active}
                  onCheckedChange={(v) => setFormData({ ...formData, is_active: v })}
                />
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => { resetForm(); setShowCreateDialog(false); }}>Cancel</Button>
            <Button onClick={handleCreateOrUpdate}>
              {editingReport ? 'Update Schedule' : 'Create Schedule'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ScheduledReportsPage;
